import React from 'react';
import { tx } from '@/translations/translations';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, Package, Ruler } from 'lucide-react';
import { ProductSizeGuide } from '../ProductSizeGuide';

const labelImageSrc = (product, isKidsSize, language) => {
  // Bamboo products use a dedicated label image (only for adult sizes — kids fall back to generic kid label)
  if (product.material === 'bambu' && !isKidsSize) {
    return language === 'es' ? '/products/feature-printed-label-bamboo-es.webp' : '/products/feature-printed-label-bamboo-en.webp';
  }
  if (isKidsSize) {
    return language === 'es' ? '/products/feature-printed-label-kid-es.webp' : '/products/feature-printed-label-kid-en.webp';
  }
  return language === 'es' ? '/products/feature-printed-label-es.webp' : '/products/feature-printed-label-en.webp';
};

const FeatureRow = ({ feature, small }) => (
  <div className="flex items-start space-x-2">
    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
    <span className={`text-xs ${small ? 'sm:text-sm' : 'sm:text-base'} text-slate-700`}>{feature}</span>
  </div>
);

const CareRow = ({ es, en, he, language }) => (
  <div className="flex items-start space-x-2">
    <Package className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600 mt-0.5 flex-shrink-0" />
    <span className="text-xs sm:text-base text-slate-700">{tx(language, es, en, he)}</span>
  </div>
);

export const ProductTabs = ({ product, language, isKidsSize, activeTab, setActiveTab }) => (
  <div className="border-t border-slate-100 px-3 sm:px-6 md:px-8 pb-3 sm:pb-6">
    <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2 sm:mt-4">
      <TabsList className={`grid w-full ${product.style !== 'Talit Gadol' ? 'grid-cols-4' : 'grid-cols-3'} h-auto`}>
        <TabsTrigger value="features" className="text-[11px] sm:text-sm px-1 sm:px-3 py-1.5">
          {tx(language, 'Características', 'Features', 'מאפיינים')}
        </TabsTrigger>
        <TabsTrigger value="sizes" data-testid="tab-size-guide" className="text-[11px] sm:text-sm px-1 sm:px-3 py-1.5">
          <Ruler className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
          {tx(language, 'Guía', 'Sizes', 'מידות')}
        </TabsTrigger>
        {product.style !== 'Talit Gadol' && (
          <TabsTrigger value="label" data-testid="tab-label" className="text-[11px] sm:text-sm px-1 sm:px-3 py-1.5">
            {tx(language, 'Etiqueta', 'Label', 'תווית')}
          </TabsTrigger>
        )}
        <TabsTrigger value="care" className="text-[11px] sm:text-sm px-1 sm:px-3 py-1.5">
          {tx(language, 'Cuidados', 'Care', 'טיפול')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="features" className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
        {product.style === 'Tank Top' ? (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 items-center">
            <div className="rounded-xl overflow-hidden border border-slate-200 bg-white sm:col-span-3" data-testid="tank-openings-feature">
              <img
                src={language === 'es' ? '/products/tank-feature-openings-es.webp' : '/products/tank-feature-openings-en.webp'}
                alt={tx(language, 'Cuello amplio y axilas abiertas', 'Relaxed neck & arm openings', 'פתח צוואר רחב ובתי שחי פתוחים')}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <div className="space-y-2 sm:space-y-2.5 sm:col-span-1">
              {product.features.map((feature) => <FeatureRow key={feature} feature={feature} small />)}
            </div>
          </div>
        ) : (
          product.features.map((feature) => <FeatureRow key={feature} feature={feature} />)
        )}
      </TabsContent>

      {product.style !== 'Talit Gadol' && (
        <TabsContent value="label" data-testid="label-tab-content" className="mt-3 sm:mt-4">
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-white" data-testid="printed-label-feature">
            <img
              src={labelImageSrc(product, isKidsSize, language)}
              alt={tx(language, 'Etiqueta impresa Mitzva-Fit', 'Mitzva-Fit printed label', 'תווית מודפסת Mitzva-Fit')}
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <p className="text-xs sm:text-sm text-slate-600 mt-3 italic text-center">
            {tx(language, 'Toda nuestra prenda tiene etiqueta impresa: más comodidad, cero rozaduras.', 'All our garments feature printed labels: more comfort, zero irritation.', 'כל הבגדים שלנו עם תווית מודפסת: יותר נוחות, אפס שפשופים.')}
          </p>
        </TabsContent>
      )}

      <ProductSizeGuide product={product} language={language} />

      <TabsContent value="care" className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
        <CareRow language={language}
          es="Lavar según las instrucciones específicas del material"
          en="Wash according to material-specific instructions"
          he="יש לכבס לפי ההוראות הספציפיות של הבד" />
        <CareRow language={language}
          es="Evitar exposición prolongada al sol directo"
          en="Avoid prolonged exposure to direct sunlight"
          he="להימנע מחשיפה ממושכת לשמש ישירה" />
        <CareRow language={language}
          es="Guardar en lugar seco y limpio cuando no se use"
          en="Store in a dry and clean place when not in use"
          he="לאחסן במקום יבש ונקי כשאינו בשימוש" />
      </TabsContent>
    </Tabs>
  </div>
);
